import { Dictionary } from 'lodash';
import React, { ReactNode, useCallback, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { AccountStatus } from 'api/account';
import { User } from 'api/models';
import { PartnerCompany } from 'api/partner/types';
import { confirm } from 'components/atoms/modal';
import { toastService } from 'components/molecules/toast';
import { useAccount, useBillingFeatureFlag } from 'hooks/use-account';
import { useTranslate } from 'hooks/use-translation';
import {
  SUBSCRIPTION_KEY,
  useAvailableLicensesQuery,
  useForceSyncSubscriptionMutation,
  useSubscriptionQuery,
} from 'queries/billing';
import { ALL_MEMBERS_QUERY_KEY } from 'queries/members';
import { useUpdatePartnerCompanyMutation } from 'queries/partner';

const content = (headingText: ReactNode, confirmationText: ReactNode) => (
  <div>
    <span>{headingText}</span>
    <br />
    <br />
    <span>{confirmationText}</span>
  </div>
);

export enum ReactivateStatus {
  Reactivating = 'reactivating',
  Reactivated = 'reactivated',
  Failed = 'failed',
}

interface Result {
  changeActiveStatus: (partner: PartnerCompany, active: boolean) => void;
  partnerForReactivatingBillingFlow?: PartnerCompany;
  reactivateStatus: Dictionary<ReactivateStatus>;
  onBillingFlowConfirm: () => void;
  onBillingFlowClose: () => void;
  adminForReactivatePartnerCompany?: User;
}

export const usePartnerStatusActions = (): Result => {
  const translate = useTranslate();
  const { data: subscription } = useSubscriptionQuery();
  const { mutateAsync: forceSyncSubscription } =
    useForceSyncSubscriptionMutation();
  const queryClient = useQueryClient();
  const { status: accountStatus } = useAccount();
  const isBillingEnabled = useBillingFeatureFlag();
  const availableLicenses = useAvailableLicensesQuery();
  const [reactivateStatus, setReactivateStatus] = useState<
    Dictionary<ReactivateStatus>
  >({});

  const availableLicensesRef = useRef<number>(availableLicenses);

  availableLicensesRef.current = availableLicenses;
  const [
    partnerForReactivatingBillingFlow,
    setPartnerForReactivatingBillingFlow,
  ] = useState<PartnerCompany>();

  const { mutate: updatePartner } = useUpdatePartnerCompanyMutation();
  const activatePartner = useCallback(
    (partner: PartnerCompany) => {
      setReactivateStatus((previous) => ({
        ...previous,
        [partner.companyId]: ReactivateStatus.Reactivating,
      }));
      updatePartner(
        {
          ...partner,
          active: true,
        },
        {
          onSuccess: async () => {
            if (subscription?.subscriptionId != null) {
              await forceSyncSubscription(subscription.subscriptionId);
            }
            await queryClient.invalidateQueries(SUBSCRIPTION_KEY);
            await queryClient.invalidateQueries(ALL_MEMBERS_QUERY_KEY);
            setReactivateStatus((previous) => ({
              ...previous,
              [partner.companyId]: ReactivateStatus.Reactivated,
            }));
            toastService({
              title: translate('success'),
              message: translate('partners.activation.success.message'),
            }).success();
          },
          onError: () => {
            setReactivateStatus((previous) => ({
              ...previous,
              [partner.companyId]: ReactivateStatus.Failed,
            }));
            toastService({
              title: translate('failure'),
              message: translate('partners.activation.failure.message'),
            }).error();
          },
        },
      );
    },
    [
      forceSyncSubscription,
      queryClient,
      subscription?.subscriptionId,
      translate,
      updatePartner,
    ],
  );
  const deactivatePartner = useCallback(
    (partner: PartnerCompany) => {
      updatePartner(
        {
          ...partner,
          active: false,
        },
        {
          onSuccess: async () => {
            if (subscription?.subscriptionId != null) {
              await forceSyncSubscription(subscription.subscriptionId);
            }
            await queryClient.invalidateQueries(SUBSCRIPTION_KEY);
            await queryClient.invalidateQueries(ALL_MEMBERS_QUERY_KEY);
            toastService({
              title: translate('success'),
              message: translate('partners.deactivation.success.message'),
            }).success();
          },
          onError: () => {
            toastService({
              title: translate('failure'),
              message: translate('partners.deactivation.failure.message'),
            }).error();
          },
        },
      );
    },
    [
      forceSyncSubscription,
      queryClient,
      subscription?.subscriptionId,
      translate,
      updatePartner,
    ],
  );
  const confirmDeactivatePartner = useCallback(
    (partner: PartnerCompany) => {
      confirm({
        title: translate('partners.inactive.confirmation.title'),
        content: content(
          translate('partners.inactive.confirmation.description_header', {
            companyName: partner.companyName,
            b: (chunks) => <strong>{chunks}</strong>,
          }),
          translate('partners.inactive.confirmation.description_footer'),
        ),
        okText: translate('partners.inactive.confirmation.confirm_button'),
        okButtonProps: { type: 'primary', danger: true },
        onOk: () => {
          deactivatePartner(partner);
        },
      });
    },
    [deactivatePartner, translate],
  );
  const beforeActivatingPartner = useCallback(
    (partner: PartnerCompany) => {
      if (
        isBillingEnabled &&
        accountStatus !== AccountStatus.Trial &&
        availableLicensesRef.current <= 0
      ) {
        setPartnerForReactivatingBillingFlow(partner);
      } else {
        activatePartner(partner);
      }
    },
    [accountStatus, activatePartner, isBillingEnabled],
  );
  const changeActiveStatus = useCallback(
    (partner: PartnerCompany, active: boolean) => {
      if (!active) {
        confirmDeactivatePartner(partner);
      } else {
        beforeActivatingPartner(partner);
      }
    },
    [beforeActivatingPartner, confirmDeactivatePartner],
  );
  const onBillingFlowClose = useCallback(() => {
    if (partnerForReactivatingBillingFlow != null) {
      setReactivateStatus((previous) => ({
        ...previous,
        [partnerForReactivatingBillingFlow.companyId]: undefined,
      }));
      setPartnerForReactivatingBillingFlow(undefined);
    }
  }, [partnerForReactivatingBillingFlow]);
  const onBillingFlowConfirm = useCallback(() => {
    if (partnerForReactivatingBillingFlow != null) {
      activatePartner(partnerForReactivatingBillingFlow);
    }
  }, [activatePartner, partnerForReactivatingBillingFlow]);

  return {
    changeActiveStatus,
    partnerForReactivatingBillingFlow,
    reactivateStatus,
    onBillingFlowConfirm,
    onBillingFlowClose,
    adminForReactivatePartnerCompany:
      partnerForReactivatingBillingFlow?.admins?.[0],
  };
};
